import PropTypes from "prop-types";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";

// Scan POV
export const SCAN_FOR_SELF = "self";
export const SCAN_FOR_OTHER = "other";
export const SCAN_USER_SET = "user";
const SCAN_POV_OPTIONS = [
  {
    id: SCAN_FOR_SELF,
    label: "For yourself",
    image: `${process.env.PUBLIC_URL}/prescan/scan-self.png`,
  },
  {
    id: SCAN_FOR_OTHER,
    label: "For someone else",
    image: `${process.env.PUBLIC_URL}/prescan/scan-someone-else.png`,
  },
];

// Paper types
export const PAPER_TYPE_US_LETTER = "us-letter";
export const PAPER_TYPE_A4 = "a4";
export const PAPER_TYPES = [PAPER_TYPE_US_LETTER, PAPER_TYPE_A4];

// Selected color
const selectedColor = (selected) => {
  return selected ? "primary.main" : "text.secondary";
};

// The view component
export const PrescanConfigureScreen = ({
  mayUsePaper,
  scanPov,
  paperType,
  onScanPovSet,
  onPaperTypeSet,
}) => {
  return (
    <Stack spacing={4} sx={{ flex: 1, minHeight: 0, px: 2, overflowY: "auto" }}>
      <Title />
      <ScanPovSelector target={scanPov} onTap={onScanPovSet} />
      {mayUsePaper && (
        <PaperTypeSelector type={paperType} onChange={onPaperTypeSet} />
      )}
    </Stack>
  );
};
PrescanConfigureScreen.propTypes = {
  mayUsePaper: PropTypes.bool.isRequired,
  scanPov: PropTypes.oneOf([SCAN_FOR_SELF, SCAN_FOR_OTHER, SCAN_USER_SET]),
  paperType: PropTypes.oneOf(PAPER_TYPES),
  onScanPovSet: PropTypes.func.isRequired, // (SCAN_FOR_SELF | SCAN_FOR_OTHER) => void
  onPaperTypeSet: PropTypes.func.isRequired, // (paperType) => void
};

// Helper: title
const Title = () => {
  return (
    <Typography
      variant="h4"
      fontWeight="bold"
      textAlign="center"
      color="text.secondary"
    >
      Before we start...
    </Typography>
  );
};

// Helper: reusable radio mark
const RadioMark = ({ selected }) => {
  const color = selectedColor(selected);
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        border: 1,
        borderColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Filled dot indicating the card is selected */}
      {selected && (
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: "primary.main",
          }}
        />
      )}
    </Box>
  );
};
RadioMark.propTypes = {
  selected: PropTypes.bool.isRequired,
};

// Helper: scan POV setting
const ScanPovCard = ({ option, selected, onTap }) => {
  const color = selectedColor(selected);
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      onClick={onTap}
      sx={{
        border: 1,
        borderColor: color,
        borderRadius: 6,
        flex: 1,
      }}
    >
      <Stack sx={{ py: 1 }}>
        {/* Radio mark affixed to the top left */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
          }}
        >
          <RadioMark selected={selected} />
        </Box>

        {/* Image */}
        <Box
          component="img"
          src={option.image}
          alt=""
          sx={{ height: 100, objectFit: "contain", mt: 4 }}
        />

        {/* Label */}
        <Typography variant="body1" color={color}>
          {option.label}
        </Typography>
      </Stack>
    </ButtonBase>
  );
};
ScanPovCard.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
  onTap: PropTypes.func.isRequired,
};
const ScanPovSelector = ({ target, onTap }) => {
  return (
    <Stack spacing={2}>
      <Typography variant="body1" fontWeight="bold">
        Please tell us if you are scanning
      </Typography>
      <Stack direction="row" spacing={2}>
        {SCAN_POV_OPTIONS.map((option) => (
          <ScanPovCard
            key={option.id}
            option={option}
            selected={target === option.id}
            onTap={() => onTap(option.id)}
          />
        ))}
      </Stack>
    </Stack>
  );
};
ScanPovSelector.propTypes = {
  target: PropTypes.string,
  onTap: PropTypes.func.isRequired,
};

// Helper: paper type setting
const PaperTypeCard = ({ type, selected, onTap }) => {
  const typeLabel = () => {
    switch (type) {
      case PAPER_TYPE_US_LETTER:
        return "US Letter";
      case PAPER_TYPE_A4:
        return "A4";
      default:
        return null;
    }
  };
  const color = selectedColor(selected);
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      onClick={onTap}
      sx={{
        width: "100%",
        borderRadius: 6,
        border: 1,
        borderColor: color,
        display: "flex",
        justifyContent: "flex-start",
      }}
    >
      <Stack direction="row" gap={2} sx={{ p: 2 }}>
        <RadioMark selected={selected} />
        <Typography
          color={color}
          sx={{ display: "flex", alignItems: "center" }}
        >
          {typeLabel()}
        </Typography>
      </Stack>
    </ButtonBase>
  );
};
PaperTypeCard.propTypes = {
  type: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onTap: PropTypes.func.isRequired,
};
const PaperTypeSelector = ({ type, onChange }) => {
  return (
    <Stack spacing={2}>
      <Typography variant="body1" fontWeight="bold">
        And if you are using
      </Typography>
      <Stack spacing={1} sx={{ pt: 1 }}>
        {PAPER_TYPES.map((paperType) => (
          <PaperTypeCard
            key={paperType}
            type={paperType}
            selected={type === paperType}
            onTap={() => onChange(paperType)}
          />
        ))}
      </Stack>
    </Stack>
  );
};
PaperTypeSelector.propTypes = {
  type: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
